/**
 * Directional edge flags — bitfield.
 * A tile with `EdgeDir.N` blocks movement *into* it from the north
 * (i.e. blocks a step where dy = -1 into this tile).
 */
export const EdgeDir = {
  N:  0b0001,
  E:  0b0010,
  S:  0b0100,
  W:  0b1000,
} as const;

export type EdgeFlags = number; // bitmask of EdgeDir values

/**
 * Map a (dx, dy) step direction to the edge flag that would be tested
 * on the *destination* tile.  E.g. stepping dx=0,dy=-1 (moving north)
 * arrives from the south, so the destination's S edge is the gate.
 */
export function edgeFlagForStep(dx: number, dy: number): EdgeFlags {
  let flag = 0;
  if (dy < 0) flag |= EdgeDir.S;  // arriving from south
  if (dy > 0) flag |= EdgeDir.N;  // arriving from north
  if (dx > 0) flag |= EdgeDir.W;  // arriving from west
  if (dx < 0) flag |= EdgeDir.E;  // arriving from east
  return flag;
}

/**
 * Tile-based collision grid.  `blocked[y][x] = true` means fully impassable.
 * `edges[y][x]` stores directional edge flags for half-tile blocking.
 * Built once at map creation, queried every frame during movement.
 */
export class CollisionMap {
  private readonly blocked: boolean[][];
  private readonly edges: EdgeFlags[][];
  private readonly _size: number;
  /** Incremented on every mutation so consumers can detect changes. */
  private _revision = 0;

  constructor(
    waterMap: boolean[][],
    treePositions: Array<{ worldX: number; worldY: number }>,
    solidProps: Array<[number, number]>,
    size?: number,
  ) {
    this._size = size ?? waterMap.length;
    this.blocked = [];
    this.edges = [];
    for (let y = 0; y < this._size; y++) {
      this.blocked[y] = [];
      this.edges[y] = [];
      for (let x = 0; x < this._size; x++) {
        this.blocked[y]![x] = waterMap[y]?.[x] ?? false;
        this.edges[y]![x] = 0;
      }
    }

    for (const tree of treePositions) {
      const tx = Math.floor(tree.worldX);
      const ty = Math.floor(tree.worldY);
      if (this.inBounds(tx, ty)) this.blocked[ty]![tx] = true;
    }

    for (const [tx, ty] of solidProps) {
      if (this.inBounds(tx, ty)) this.blocked[ty]![tx] = true;
    }
  }

  static fromGrid(blocked: boolean[][]): CollisionMap {
    return new CollisionMap(blocked, [], []);
  }

  get revision(): number {
    return this._revision;
  }

  get size(): number {
    return this._size;
  }

  /** Returns true if the tile at the given world position is fully blocked. */
  isBlocked(x: number, y: number): boolean {
    const tx = Math.floor(x);
    const ty = Math.floor(y);
    if (!this.inBounds(tx, ty)) return true;
    return this.blocked[ty]![tx]!;
  }

  /** Returns true if the tile at integer indices is fully blocked (no floor). */
  isBlockedAt(tx: number, ty: number): boolean {
    if (!this.inBounds(tx, ty)) return true;
    return this.blocked[ty]![tx]!;
  }

  /**
   * Check whether stepping from (fromX, fromY) to (toX, toY) is blocked.
   * Accounts for both full tile blocks and directional edge flags.
   */
  isStepBlocked(fromX: number, fromY: number, toX: number, toY: number): boolean {
    const ttx = Math.floor(toX);
    const tty = Math.floor(toY);
    if (!this.inBounds(ttx, tty)) return true;
    if (this.blocked[tty]![ttx]) return true;

    // Check directional edge
    const dx = Math.sign(Math.floor(toX) - Math.floor(fromX));
    const dy = Math.sign(Math.floor(toY) - Math.floor(fromY));
    if (dx === 0 && dy === 0) return false; // same tile
    const flag = edgeFlagForStep(dx, dy);
    return (this.edges[tty]![ttx]! & flag) !== 0;
  }

  /** Get edge flags for a tile. */
  getEdges(tx: number, ty: number): EdgeFlags {
    if (!this.inBounds(tx, ty)) return 0;
    return this.edges[ty]![tx]!;
  }

  /** Set directional edge flags on a tile (bitmask of EdgeDir values). */
  setEdges(tx: number, ty: number, flags: EdgeFlags): void {
    if (!this.inBounds(tx, ty)) return;
    this.edges[ty]![tx] = flags;
    this._revision++;
  }

  /** Add edge flags to a tile (OR with existing). */
  addEdge(tx: number, ty: number, flags: EdgeFlags): void {
    if (!this.inBounds(tx, ty)) return;
    this.edges[ty]![tx]! |= flags;
    this._revision++;
  }

  /** Remove specific edge flags from a tile. */
  removeEdge(tx: number, ty: number, flags: EdgeFlags): void {
    if (!this.inBounds(tx, ty)) return;
    this.edges[ty]![tx]! &= ~flags;
    this._revision++;
  }

  /** Dynamically mark a tile as fully blocked or walkable at runtime. */
  setBlocked(x: number, y: number, value: boolean): void {
    const tx = Math.floor(x);
    const ty = Math.floor(y);
    if (!this.inBounds(tx, ty)) return;
    this.blocked[ty]![tx] = value;
    this._revision++;
  }

  private inBounds(tx: number, ty: number): boolean {
    return tx >= 0 && tx < this._size && ty >= 0 && ty < this._size;
  }
}
